/**
 * Tilemap basata su griglia ASCII: leggibile nei diff, facile da editare a
 * mano e da generare proceduralmente in futuro. Il movimento del giocatore è
 * libero (pixel-based); la griglia serve per collisioni e zone di incontro.
 */

export const TILE_SIZE = 32;

export enum Tile {
  Grass = 'grass',
  TallGrass = 'tallGrass',
  Path = 'path',
  Water = 'water',
  Tree = 'tree',
  Rock = 'rock',
  Flower = 'flower',
  Anomaly = 'anomaly',
  HealStation = 'healStation',
  HouseWall = 'houseWall',
  Sand = 'sand',
}

const CHAR_TO_TILE: Record<string, Tile> = {
  '.': Tile.Grass,
  ',': Tile.TallGrass,
  '#': Tile.Path,
  '~': Tile.Water,
  T: Tile.Tree,
  O: Tile.Rock,
  f: Tile.Flower,
  A: Tile.Anomaly,
  H: Tile.HealStation,
  h: Tile.HouseWall,
  s: Tile.Sand,
};

const SOLID_TILES = new Set<Tile>([
  Tile.Water,
  Tile.Tree,
  Tile.Rock,
  Tile.HealStation,
  Tile.HouseWall,
]);
const ENCOUNTER_TILES = new Set<Tile>([Tile.TallGrass, Tile.Anomaly]);

export interface TileMap {
  width: number;
  height: number;
  tiles: Tile[]; // row-major
}

export function parseAsciiMap(ascii: string): TileMap {
  const rows = ascii
    .split('\n')
    .map((r) => r.trimEnd())
    .filter((r) => r.length > 0);
  if (rows.length === 0) throw new Error('Mappa vuota');
  const width = rows[0]!.length;
  if (rows.some((r) => r.length !== width)) {
    throw new Error('Tutte le righe della mappa devono avere la stessa larghezza');
  }
  const tiles: Tile[] = [];
  for (const row of rows) {
    for (const char of row) {
      const tile = CHAR_TO_TILE[char];
      if (!tile) throw new Error(`Carattere mappa sconosciuto: "${char}"`);
      tiles.push(tile);
    }
  }
  return { width, height: rows.length, tiles };
}

export function tileAt(map: TileMap, tx: number, ty: number): Tile {
  if (tx < 0 || ty < 0 || tx >= map.width || ty >= map.height) return Tile.Rock; // bordo solido
  return map.tiles[ty * map.width + tx]!;
}

export function isSolid(tile: Tile): boolean {
  return SOLID_TILES.has(tile);
}

export function isEncounterTile(tile: Tile): boolean {
  return ENCOUNTER_TILES.has(tile);
}

/**
 * Collisione AABB contro la griglia: ritorna true se il rettangolo
 * (in pixel, ancorato in alto a sinistra) interseca un tile solido.
 */
export function rectCollides(
  map: TileMap,
  x: number,
  y: number,
  w: number,
  h: number,
): boolean {
  const x0 = Math.floor(x / TILE_SIZE);
  const y0 = Math.floor(y / TILE_SIZE);
  const x1 = Math.floor((x + w - 1) / TILE_SIZE);
  const y1 = Math.floor((y + h - 1) / TILE_SIZE);
  for (let ty = y0; ty <= y1; ty++) {
    for (let tx = x0; tx <= x1; tx++) {
      if (isSolid(tileAt(map, tx, ty))) return true;
    }
  }
  return false;
}

/** Tile prevalente sotto un punto (es. i piedi del giocatore). */
export function tileAtPixel(map: TileMap, px: number, py: number): Tile {
  return tileAt(map, Math.floor(px / TILE_SIZE), Math.floor(py / TILE_SIZE));
}
